// src/services/onlineStatusService.ts
import {
  doc,
  updateDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_COLLECTIONS } from '../Component/api/url';

export class OnlineStatusService {
    private usersCollection = FIREBASE_COLLECTIONS.USERS;
  private statusUpdateInterval: NodeJS.Timeout | null = null;
  private currentUserId: string | null = null;

  /**
   * Set user online status and setup automatic offline on disconnect
   */
  async setUserOnline(userId: string): Promise<void> {
    try {
      console.log('🟢 Setting user online:', userId);
      this.currentUserId = userId;

      const userDocRef = doc(db, this.usersCollection, userId);

      // Set user as online
      await updateDoc(userDocRef, {
        onlineStatus: 'online',
        lastSeen: serverTimestamp(),
        isActive: true,
      });

      // Setup periodic heartbeat to maintain online status
      this.setupHeartbeat(userId);

      // Setup offline handler for when user closes browser/tab
      this.setupOfflineHandler(userId);

      console.log('✅ User set to online successfully');
    } catch (error) {
      console.error('❌ Error setting user online:', error);
      
      // If document doesn't exist, create it
      if ((error as any)?.code === 'not-found') {
        await this.createUserDocument(userId);
        await this.setUserOnline(userId); // Retry
      }
    }
  }

  /**
   * Set user offline status
   */
  async setUserOffline(userId: string): Promise<void> {
    try {
      console.log('🔴 Setting user offline:', userId);

      const userDocRef = doc(db, this.usersCollection, userId);

      await updateDoc(userDocRef, {
        onlineStatus: 'offline',
        lastSeen: serverTimestamp(),
        isActive: false,
      });

      // Clear heartbeat interval
      if (this.statusUpdateInterval) {
        clearInterval(this.statusUpdateInterval);
        this.statusUpdateInterval = null;
      }

      this.currentUserId = null;

      console.log('✅ User set to offline successfully');
    } catch (error) {
      console.error('❌ Error setting user offline:', error);
    }
  }

  /**
   * Setup periodic heartbeat to keep user online
   */
  private setupHeartbeat(userId: string): void {
    // Clear any existing interval
    if (this.statusUpdateInterval) {
      clearInterval(this.statusUpdateInterval);
    }

    // Update online status every 30 seconds
    this.statusUpdateInterval = setInterval(async () => {
      try {
        const userDocRef = doc(db, this.usersCollection, userId);
        await updateDoc(userDocRef, {
          lastSeen: serverTimestamp(),
          onlineStatus: 'online',
        });
        console.log('💓 Heartbeat sent for user:', userId);
      } catch (error) {
        console.error('❌ Error sending heartbeat:', error);
      }
    }, 30000); // 30 seconds
  }

  /**
   * Setup handlers for browser close/refresh
   */
  private setupOfflineHandler(userId: string): void {
    // Handle page unload (close/refresh)
    const handleBeforeUnload = async () => {
      const userDocRef = doc(db, this.usersCollection, userId);
      
      // Try to update status before page unloads
      // Note: This may not always complete due to browser restrictions
      try {
        await updateDoc(userDocRef, {
          onlineStatus: 'offline',
          lastSeen: serverTimestamp(),
          isActive: false,
        });
      } catch (error) {
        console.error('Error in beforeunload:', error);
      }
    };

    // Handle visibility change (tab hidden/shown)
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Tab is hidden - set as away
        try {
          const userDocRef = doc(db, this.usersCollection, userId);
          await updateDoc(userDocRef, {
            onlineStatus: 'away',
            lastSeen: serverTimestamp(),
          });
          console.log('👀 User set to away (tab hidden)');
        } catch (error) {
          console.error('Error setting away status:', error);
        }
      } else {
        // Tab is visible again - set back to online
        try {
          const userDocRef = doc(db, this.usersCollection, userId);
          await updateDoc(userDocRef, {
            onlineStatus: 'online',
            lastSeen: serverTimestamp(),
          });
          console.log('👀 User back to online (tab visible)');
        } catch (error) {
          console.error('Error setting online status:', error);
        }
      }
    };

    // Handle window focus/blur
    const handleFocus = async () => {
      try {
        const userDocRef = doc(db, this.usersCollection, userId);
        await updateDoc(userDocRef, {
          onlineStatus: 'online',
          lastSeen: serverTimestamp(),
        });
        console.log('🎯 User focused - set to online');
      } catch (error) {
        console.error('Error on focus:', error);
      }
    };

    const handleBlur = async () => {
      try {
        const userDocRef = doc(db, this.usersCollection, userId);
        await updateDoc(userDocRef, {
          onlineStatus: 'away',
          lastSeen: serverTimestamp(),
        });
        console.log('😴 User blurred - set to away');
      } catch (error) {
        console.error('Error on blur:', error);
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Cleanup function (store in class if needed)
    this.cleanupHandlers = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }

  private cleanupHandlers: (() => void) | null = null;

  /**
   * Create user document if it doesn't exist
   */
  private async createUserDocument(userId: string): Promise<void> {
    try {
      const userDocRef = doc(db, this.usersCollection, userId);
      
      // Get user type
      const userType = this.getUserType(userId);
      const numericId = this.extractUserId(userId);

      const newUserData: any = {
        onlineStatus: 'offline',
        profile_url: 'https://img.freepik.com/premium-photo/portrait-serious-businesswoman-using-laptop-office_118454-14967.jpg',
        user_id: numericId,
        isActive: false,
        lastSeen: serverTimestamp(),
      };

      if (userType === 'nutritionist' || userType === 'admin') {
        const localUserData = this.getUserDataFromLocalStorage();
        if (localUserData) {
          const firstName = localUserData.nutrist_fname || 'Nutritionist';
          const lastName = localUserData.nutrist_lname || '';
          
          newUserData.user_fname = firstName;
          newUserData.user_lname = lastName;

          if (localUserData.nutrist_profile) {
            newUserData.profile_url = localUserData.nutrist_profile;
          }
        } else {
          newUserData.user_fname = userType === 'admin' ? 'Admin' : 'Nutritionist';
          newUserData.user_lname = numericId;
        }
      } else {
        newUserData.user_fname = 'User';
        newUserData.user_lname = numericId;
      }

      await setDoc(userDocRef, newUserData);
      console.log('✅ User document created:', userId);
    } catch (error) {
      console.error('❌ Error creating user document:', error);
    }
  }

  /**
   * Extract user ID from combined ID format
   */
  private extractUserId(fullId: string): string {
    const match = fullId.match(/\d+$/);
    return match ? match[0] : fullId;
  }

  /**
   * Get user type from ID
   */
  private getUserType(fullId: string): 'user' | 'nutritionist' | 'admin' {
    if (fullId.startsWith('userid_')) return 'user';
    if (fullId.startsWith('nutritionistid_')) return 'nutritionist';
    if (fullId.startsWith('adminid_')) return 'admin';
    return 'user';
  }

  /**
   * Get user data from localStorage
   */
  private getUserDataFromLocalStorage() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    return null;
  }

  /**
   * Cleanup all listeners and intervals
   */
  cleanup(): void {
    if (this.statusUpdateInterval) {
      clearInterval(this.statusUpdateInterval);
      this.statusUpdateInterval = null;
    }

    if (this.cleanupHandlers) {
      this.cleanupHandlers();
      this.cleanupHandlers = null;
    }

    if (this.currentUserId) {
      // Set user offline before cleanup
      this.setUserOffline(this.currentUserId);
    }
  }
}

// Export singleton instance
export const onlineStatusService = new OnlineStatusService();